/**
 * finaleState.ts
 * Types, constants, and module-level state for the Level 1-1 finale cinematic.
 *
 * Extracted from FinaleSequence.ts to keep both files under the 300-line
 * AGENTS.md limit. The renderer reads `finaleState`; FinaleSequence writes it.
 *
 * Phase 3 — Art, Audio & Polish.
 *
 * @module finaleState
 */

import { TILE_SIZE } from '../../constants/gameConstants';
import type { FlameMode } from '../rendering/SpriteRenderer';

// ── Beat identifiers ──────────────────────────────────────────────────────

/** All possible beats in the finale cinematic. */
export type FinaleBeat =
  | 'NONE'
  | 'APPROACH'
  | 'GATE_OPENING'
  | 'TRAVERSE'
  | 'WALK_THROUGH'
  | 'RAMP_RAISING'
  | 'WALK_TO_SHIP'
  | 'BOARDING'
  | 'IGNITION'
  | 'TAKEOFF'
  | 'OFFSCREEN'
  | 'FADE_OUT';

// ── Beat durations (seconds) ──────────────────────────────────────────────

export const GATE_OPEN_DUR    = 0.8;
export const WALK_THROUGH_DUR = 1.5;
export const RAMP_RAISE_DUR   = 1.5;
export const WALK_TO_SHIP_DUR = 1.5;
export const BOARDING_DUR     = 1.0;
export const IGNITION_DUR     = 1.5;
export const TAKEOFF_DUR      = 1.0;
export const OFFSCREEN_DUR    = 2.0;
export const FADE_OUT_DUR     = 1.0;

// ── Layout constants ──────────────────────────────────────────────────────

/** Gate column — set by FinaleSequence.init(). */
export let GATE_COL = 432;
/** Ramp left column — set by FinaleSequence.init(). */
export let RAMP_COL = 438;

/**
 * Update GATE_COL and RAMP_COL from FinaleSequence.init().
 * @param gateCol - Gate left-edge column.
 * @param rampCol - Ramp left-edge column.
 */
export function setFinaleColumns(gateCol: number, rampCol: number): void {
  GATE_COL = gateCol;
  RAMP_COL = rampCol;
}

/** Gate world X (left edge of the 3-tile-wide archway). */
export const gateX = (): number => GATE_COL * TILE_SIZE;
/** Gate is 3 tiles wide. */
export const GATE_W = 3 * TILE_SIZE;
/** Gate is 4 tiles tall. */
export const GATE_H = 4 * TILE_SIZE;
/** Ground row for gate/ramp area. */
export const GROUND_ROW = 14;
/** Ship pad surface row — same as ground (terrain is flat to the ship). */
export const SHIP_PAD_ROW = 14;
/** Gate base Y (bottom of archway sits on ground surface). */
export const gateBaseY = (): number => GROUND_ROW * TILE_SIZE;
/** Gate top Y. */
export const gateTopY = (): number => gateBaseY() - GATE_H;
/** Action prompt range — within 2.5 tiles of the pedestal centre. */
export const PROMPT_RANGE = TILE_SIZE * 2.5;

// ── Portal & pedestal layout (must match webTileDraw.web.ts) ──────────────

/** Portal column offset left of GATE_COL. */
export const PORTAL_OFFSET_TILES = 8;
/** Portal draw width in pixels (5 tiles). */
export const PORTAL_DRAW_W = 5 * TILE_SIZE;
/** Pedestal column offset left of portal (4 tiles). */
export const PEDESTAL_OFFSET_TILES = 4;
/** Pedestal draw width in pixels (3 tiles). */
export const PEDESTAL_DRAW_SIZE = 3 * TILE_SIZE;

/** Portal world X (left edge). */
export const portalX = (): number => (GATE_COL - PORTAL_OFFSET_TILES) * TILE_SIZE;
/** Portal right-edge world X — used as the collision wall. */
export const portalRightX = (): number => portalX() + PORTAL_DRAW_W;
/** Pedestal world X (left edge). */
export const pedestalX = (): number => portalX() - PEDESTAL_OFFSET_TILES * TILE_SIZE;
/** Pedestal centre world X — used for proximity prompt. */
export const pedestalCentreX = (): number => pedestalX() + PEDESTAL_DRAW_SIZE / 2;
/** Ramp width in tiles. */
export const RAMP_TILES = 5;
export const rampX = (): number => RAMP_COL * TILE_SIZE;
export const RAMP_W = RAMP_TILES * TILE_SIZE;
/** Ramp surface thickness in pixels (visual bar height drawn by renderer). */
export const RAMP_THICKNESS = 6;
/** Ship sprite frame dimensions (px). */
export const SHIP_FRAME_W = 128;
export const SHIP_FRAME_H = 48;
/**
 * Transparent padding below the visible hull in the ship sprite frame.
 * DECISION: nova_ship.png has less bottom padding than the old stardrifter.
 */
export const SHIP_HULL_PAD_BOTTOM = 6;
/** Ship pad surface Y (top of the raised platform). */
export const shipPadY = (): number => SHIP_PAD_ROW * TILE_SIZE;
/** Ship parked X — centred on ramp. */
export const shipParkedX = (): number => rampX() + (RAMP_W - SHIP_FRAME_W) / 2;
/**
 * Ship parked Y — hull bottom sits on ramp surface.
 * Ramp surface = shipPadY - RAMP_THICKNESS. Visible hull bottom =
 * shipY + SHIP_FRAME_H - SHIP_HULL_PAD_BOTTOM. Solve for shipY.
 */
export const shipParkedY = (): number =>
  shipPadY() - RAMP_THICKNESS - (SHIP_FRAME_H - SHIP_HULL_PAD_BOTTOM);
/** Auto-walk speed (px/s) — a walking pace, not a run. */
export const AUTO_WALK_SPEED = 60;

// ── Finale state (module-level, read by renderer) ─────────────────────────

/** Readable finale state for the renderer. */
export interface FinaleStateData {
  beat: FinaleBeat;
  /** Seconds elapsed in current beat. */
  timer: number;
  /** Gate door open progress 0..1. */
  gateOpen: number;
  /** Gate world X (left edge). */
  gateX: number;
  /** Gate top Y. */
  gateTopY: number;
  /** Gate width px. */
  gateW: number;
  /** Gate height px. */
  gateH: number;
  /** Ramp tilt angle in degrees 0..45. */
  rampAngle: number;
  /** Ramp world X. */
  rampX: number;
  /** Ramp width px. */
  rampW: number;
  /** Ramp base Y (ground). */
  rampBaseY: number;
  /** Ship world X. */
  shipX: number;
  /** Ship world Y. */
  shipY: number;
  /** Ship rotation angle degrees (0 = horizontal, 45 = launch). */
  shipAngle: number;
  /** Ship visible (false after exiting screen). */
  shipVisible: boolean;
  /** Fade to black alpha 0..1. */
  fadeAlpha: number;
  /** Screen shake offset px. */
  shakeX: number;
  shakeY: number;
  /** Show action button prompt above Nova. */
  showActionPrompt: boolean;
  /** Show skip cutscene prompt. */
  showSkipPrompt: boolean;
  /** Sonic boom ring progress 0..1 (0 = not started). */
  sonicBoom: number;
  /** Dust particles active. */
  dustActive: boolean;
  /** Crystal glow intensity 0..1. */
  crystalGlow: number;
  /** Engine flame mode for the renderer. */
  flameMode: FlameMode;
  /** Cross-fade progress between flame states 0..1 (0 = fully previous). */
  flameCrossfade: number;
  /** Camera locked X. */
  lockedCamX: number;
  /** Camera locked Y. */
  lockedCamY: number;
  /** Whether camera is locked. */
  cameraLocked: boolean;
}

// DECISION: Module-level mutable state so the renderer can import and read
// without coupling to the FinaleSequence class instance.
export const finaleState: FinaleStateData = createDefaultFinaleState();

/** Create a fresh default FinaleStateData. */
export function createDefaultFinaleState(): FinaleStateData {
  return {
    beat: 'NONE',
    timer: 0,
    gateOpen: 0,
    gateX: 0,
    gateTopY: 0,
    gateW: GATE_W,
    gateH: GATE_H,
    rampAngle: 0,
    rampX: 0,
    rampW: RAMP_W,
    rampBaseY: 0,
    shipX: 0,
    shipY: 0,
    shipAngle: 0,
    shipVisible: true,
    fadeAlpha: 0,
    shakeX: 0,
    shakeY: 0,
    showActionPrompt: false,
    showSkipPrompt: false,
    sonicBoom: 0,
    dustActive: false,
    crystalGlow: 0,
    flameMode: 'NONE',
    flameCrossfade: 1,
    lockedCamX: 0,
    lockedCamY: 0,
    cameraLocked: false,
  };
}

/** Reset finaleState to defaults. */
export function resetFinaleState(): void {
  Object.assign(finaleState, createDefaultFinaleState());
}

// ── Easing ────────────────────────────────────────────────────────────────

/** Ease-out quadratic. */
export function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

/**
 * Convert ship position from ramp-rotated coordinates to world coordinates
 * for free flight after ignition at 45°.
 * @returns World-space {x, y} for the ship's top-left corner.
 */
export function ignitionToWorldCoords(): { x: number; y: number } {
  const a45 = (45 * Math.PI) / 180;
  const cos45 = Math.cos(a45);
  const sin45 = Math.sin(a45);
  const dx = shipParkedX() - rampX() + SHIP_FRAME_W / 2;
  const dy = shipParkedY() - shipPadY() + SHIP_FRAME_H / 2;
  const cx = rampX() + dx * cos45 + dy * sin45;
  const cy = shipPadY() - dx * sin45 + dy * cos45;
  return { x: cx - SHIP_FRAME_W / 2, y: cy - SHIP_FRAME_H / 2 };
}
